// src/Components/upload/UploadSection.tsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Flex,
  FormLabel,
  Image,
  Input,
  Spinner,
  Text,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";

import { getCaptcha, getToken, submitFile } from "../../Utils/frontStageApi";

import refreshIcon from "../../Resources/Images/Icons/refresh_icon.svg";
import uploadOkIcon from "../../Resources/Images/Icons/upload_ok.svg";
import uploadFailIcon from "../../Resources/Images/Icons/upload_fail.svg";
import verifySuccessIcon from "../../Resources/Images/Icons/veri_success.svg";
import verifyFailIcon from "../../Resources/Images/Icons/veri_fail.svg";
import verifyWarningIcon from "../../Resources/Images/Icons/veri_warning.svg";

const MAIN_HOST = (window as any).env.MAIN_HOST;

enum Estatus {
  upload = 0,
  status,
  result,
}

enum EuploadStatus {
  normal = 0,
  fail,
  overSize,
}

enum Eresult {
  null = -1,
  success = 0,
  expired = 1,
  failure = 2,
  captchaError = 3,
  JWTExpired = 4,
}

interface Metadata {
  chinese_name: string;
  student_id: string;
  graduate_univ: string;
  graduate_colg: string;
  graduate_dept: string;
  AATL: string;
}

interface VersionInfo {
  cur_ver: number;
  num_ver: number;
}

const emptyMetadata: Metadata = {
  chinese_name: "",
  student_id: "",
  graduate_univ: "",
  graduate_colg: "",
  graduate_dept: "",
  AATL: "",
};

const emptyVersionInfo: VersionInfo = {
  cur_ver: 0,
  num_ver: 0,
};

const MainPanel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Flex
    w="100%"
    maxW="1040px"
    minH="440px"
    px="24px"
    py="40px"
    bg="#FFF9D0"
    borderRadius="10px"
    align="center"
    justify="center"
  >
    {children}
  </Flex>
);

const UploadSection: React.FC<{
  status: Estatus;
  setStatus: (status: Estatus) => void;
}> = ({ status, setStatus }) => {
  const { t } = useTranslation();

  const [token, setToken] = useState<string>("");
  const [captchaID, setCaptchaID] = useState<string>("");
  const [captchaChallenge, setCaptchaChallenge] = useState<string>("");
  const [uploadFile, setUploadFile] = useState<File | undefined>(undefined);
  const [wrongFormat, setWrongFormat] = useState<boolean>(false);
  const [uploadStatus, setUploadStatus] = useState<EuploadStatus>(
    EuploadStatus.normal,
  );
  const [result, setResult] = useState<Eresult>(Eresult.null);
  const [metadata, setMetadata] = useState<Metadata>(emptyMetadata);
  const [versionInfo, setVersionInfo] = useState<VersionInfo>(emptyVersionInfo);
  const [pageLoading, setPageLoading] = useState<boolean>(false);
  const [captchaSrc, setCaptchaSrc] = useState<string>("");

  useEffect(() => {
    getToken(setToken);
  }, []);

  useEffect(() => {
    if (captchaID) {
      setCaptchaSrc(`${MAIN_HOST}/captcha/${captchaID}.jpg`);
    } else {
      setCaptchaSrc("");
    }
  }, [captchaID]);

  const resetResultData = () => {
    setResult(Eresult.null);
    setMetadata(emptyMetadata);
    setVersionInfo(emptyVersionInfo);
    setCaptchaID("");
    setCaptchaChallenge("");
  };

  const resetToFirstStep = () => {
    setUploadFile(undefined);
    setWrongFormat(false);
    setUploadStatus(EuploadStatus.normal);
    resetResultData();
    setStatus(Estatus.upload);
  };

  const isPdfFile = (file?: File) => {
    if (!file) return false;
    return (
      file.type === "application/pdf" ||
      file.name.toLowerCase().endsWith(".pdf")
    );
  };

  const handleFileSelected = (file?: File) => {
    if (!file) return;

    resetResultData();
    setWrongFormat(false);

    if (!isPdfFile(file)) {
      setUploadFile(undefined);
      setUploadStatus(EuploadStatus.fail);
      setWrongFormat(true);
      setStatus(Estatus.upload);
      return;
    }

    setUploadFile(file);
    setUploadStatus(EuploadStatus.normal);
    setStatus(Estatus.upload);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (status !== Estatus.upload) return;
    handleFileSelected(e.dataTransfer.files?.[0]);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    e.stopPropagation();
    handleFileSelected(e.target.files?.[0]);
    e.target.value = "";
  };

  const handlePrepareVerification = async () => {
    if (!uploadFile) return;

    setPageLoading(true);
    try {
      await getCaptcha(setCaptchaID);
      setCaptchaChallenge("");
      setStatus(Estatus.status);
    } finally {
      setPageLoading(false);
    }
  };

  const handleRefreshCaptcha = async () => {
    setPageLoading(true);
    try {
      await getCaptcha(setCaptchaID);
      setCaptchaChallenge("");
    } finally {
      setPageLoading(false);
    }
  };

  const handleStartVerify = async () => {
    if (!uploadFile) return;
    if (captchaID !== "" && captchaChallenge.trim().length === 0) return;

    setPageLoading(true);
    await submitFile(
      uploadFile,
      token,
      captchaID,
      captchaChallenge,
      setPageLoading,
      setStatus,
      setResult,
      setMetadata,
      setVersionInfo,
      setUploadStatus,
    );
  };

  const canSubmit = captchaID === "" || captchaChallenge.trim().length > 0;

  const resultVisual = useMemo(() => {
    if (result === Eresult.null) return null;
    switch (result) {
      case Eresult.success:
        return {
          src: verifySuccessIcon,
          title: t("upload.verifySuccess"),
          color: "#28A138",
        };
      case Eresult.expired:
        return {
          src: verifyWarningIcon,
          title: t("upload.verifyWarning"),
          color: "#ED8B00",
        };
      case Eresult.failure:
      case Eresult.captchaError:
      case Eresult.JWTExpired:
      default:
        return {
          src: verifyFailIcon,
          title: t("upload.verifyFailed"),
          color: "#E02020",
        };
    }
  }, [result, t]);

  const FileNameBox = (
    <Flex
      w="100%"
      maxW="300px"
      minH="45px"
      px="12px"
      py="12px"
      bg="#FFFFFF"
      border="1px solid #A8B8C2"
      borderRadius="3px"
      align="center"
      justify="center"
    >
      <Text
        fontFamily="Open Sans"
        fontWeight={400}
        fontSize="14px"
        lineHeight="150%"
        letterSpacing="0.01em"
        color="#4F6470"
        textAlign="center"
        noOfLines={1}
      >
        {uploadFile?.name}
      </Text>
    </Flex>
  );

  return (
    <>
      {pageLoading && (
        <Flex
          position="fixed"
          inset="0"
          bg="rgba(0, 0, 0, 0.35)"
          zIndex={2000}
          align="center"
          justify="center"
        >
          <Spinner
            size="xl"
            thickness="4px"
            color="#00AAFF"
            emptyColor="whiteAlpha.500"
          />
        </Flex>
      )}

      <Flex w="100%" direction="column" align="center" gap="30px">
        {/* 上傳區：尚未選檔 */}
        {status === Estatus.upload &&
          !uploadFile &&
          uploadStatus === EuploadStatus.normal && (
            <MainPanel>
              <Flex
                w="100%"
                direction="column"
                align="center"
                justify="center"
                gap="30px"
                onDrop={handleDrop}
                onDragOver={handleDragOver}
              >
                <Text
                  fontFamily="Open Sans"
                  fontWeight={600}
                  fontSize="20px"
                  lineHeight="120%"
                  letterSpacing="0.01em"
                  color="#000000"
                  textAlign="center"
                >
                  拖曳檔案到此
                </Text>

                <Flex align="center" justify="center" gap="12px" wrap="wrap">
                  <Text
                    fontFamily="Open Sans"
                    fontWeight={400}
                    fontSize="16px"
                    lineHeight="150%"
                    letterSpacing="0.01em"
                    color="#000000"
                  >
                    或
                  </Text>
                  <FormLabel m="0">
                    <Button
                      as="span"
                      px="12px"
                      py="8px"
                      minH="37px"
                      bg="#00AAFF"
                      color="#FFFFFF"
                      borderRadius="4px"
                      fontFamily="Open Sans"
                      fontWeight={400}
                      fontSize="14px"
                      lineHeight="150%"
                      letterSpacing="0.01em"
                      _hover={{ bg: "#00AAFF" }}
                      _active={{ bg: "#00AAFF" }}
                    >
                      瀏覽檔案
                    </Button>
                    <Input
                      type="file"
                      accept=".pdf"
                      display="none"
                      onChange={handleInputChange}
                    />
                  </FormLabel>
                </Flex>

                {wrongFormat && (
                  <Text
                    fontFamily="Open Sans"
                    fontWeight={400}
                    fontSize="16px"
                    lineHeight="150%"
                    letterSpacing="0.01em"
                    color="#E02020"
                    textAlign="center"
                  >
                    {t("upload.onlyPDF")[0]}
                  </Text>
                )}
              </Flex>
            </MainPanel>
          )}

        {/* 上傳區：已選檔，等待按上傳 */}
        {status === Estatus.upload &&
          !!uploadFile &&
          uploadStatus === EuploadStatus.normal && (
            <>
              <MainPanel>
                <Flex
                  direction="column"
                  align="center"
                  justify="center"
                  gap="30px"
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                >
                  <Image
                    src={uploadOkIcon}
                    alt="upload selected"
                    maxW="200px"
                    w="100%"
                  />
                  {FileNameBox}
                </Flex>
              </MainPanel>

              <Flex align="center" justify="center" gap="20px" wrap="wrap">
                <FormLabel m="0">
                  <Button
                    as="span"
                    px="12px"
                    py="8px"
                    minH="37px"
                    bg="#FFFFFF"
                    border="1px solid #00AAFF"
                    color="#00AAFF"
                    borderRadius="4px"
                    fontFamily="Open Sans"
                    fontWeight={400}
                    fontSize="14px"
                    lineHeight="150%"
                    letterSpacing="0.01em"
                    _hover={{ bg: "#FFFFFF" }}
                    _active={{ bg: "#FFFFFF" }}
                  >
                    重新選擇檔案
                  </Button>
                  <Input
                    type="file"
                    accept=".pdf"
                    display="none"
                    onChange={handleInputChange}
                  />
                </FormLabel>

                <Button
                  px="12px"
                  py="8px"
                  minH="37px"
                  bg="#00AAFF"
                  color="#FFFFFF"
                  borderRadius="4px"
                  fontFamily="Open Sans"
                  fontWeight={400}
                  fontSize="14px"
                  lineHeight="150%"
                  letterSpacing="0.01em"
                  _hover={{ bg: "#00AAFF" }}
                  _active={{ bg: "#00AAFF" }}
                  onClick={handlePrepareVerification}
                >
                  檔案上傳
                </Button>
              </Flex>
            </>
          )}

        {/* 上傳區：格式錯誤 */}
        {status === Estatus.upload && uploadStatus === EuploadStatus.fail && (
          <>
            <MainPanel>
              <Flex
                direction="column"
                align="center"
                justify="center"
                gap="30px"
              >
                <Image
                  src={uploadFailIcon}
                  alt="upload failed"
                  maxW="200px"
                  w="100%"
                />
                <Text
                  fontFamily="Open Sans"
                  fontWeight={600}
                  fontSize="20px"
                  lineHeight="120%"
                  letterSpacing="0.01em"
                  color="#E02020"
                  textAlign="center"
                >
                  {t("upload.uploadFailed")}
                </Text>
              </Flex>
            </MainPanel>

            <Flex justify="center">
              <Button
                px="12px"
                py="8px"
                minH="37px"
                bg="#FFFFFF"
                border="1px solid #00AAFF"
                color="#00AAFF"
                borderRadius="4px"
                fontFamily="Open Sans"
                fontWeight={400}
                fontSize="14px"
                lineHeight="150%"
                letterSpacing="0.01em"
                _hover={{ bg: "#FFFFFF" }}
                _active={{ bg: "#FFFFFF" }}
                onClick={resetToFirstStep}
              >
                重新選擇檔案
              </Button>
            </Flex>
          </>
        )}

        {/* 驗證碼輸入頁：檔案超過大小限制 */}
        {status === Estatus.status &&
          uploadStatus === EuploadStatus.overSize && (
            <>
              <MainPanel>
                <Flex
                  direction="column"
                  align="center"
                  justify="center"
                  gap="30px"
                >
                  <Image
                    src={uploadFailIcon}
                    alt="upload failed"
                    maxW="200px"
                    w="100%"
                  />
                  <Flex direction="column" align="center" gap="8px">
                    <Text
                      fontFamily="Open Sans"
                      fontWeight={600}
                      fontSize="20px"
                      lineHeight="120%"
                      letterSpacing="0.01em"
                      color="#E02020"
                      textAlign="center"
                    >
                      {t("upload.uploadFailed")}
                    </Text>
                    <Text
                      fontFamily="Open Sans"
                      fontWeight={400}
                      fontSize="16px"
                      lineHeight="150%"
                      letterSpacing="0.01em"
                      color="#E02020"
                      textAlign="center"
                    >
                      {t("upload.overSize")}
                    </Text>
                  </Flex>
                </Flex>
              </MainPanel>

              <Flex justify="center">
                <Button
                  px="12px"
                  py="8px"
                  minH="37px"
                  bg="#FFFFFF"
                  border="1px solid #00AAFF"
                  color="#00AAFF"
                  borderRadius="4px"
                  fontFamily="Open Sans"
                  fontWeight={400}
                  fontSize="14px"
                  lineHeight="150%"
                  letterSpacing="0.01em"
                  _hover={{ bg: "#FFFFFF" }}
                  _active={{ bg: "#FFFFFF" }}
                  onClick={resetToFirstStep}
                >
                  重新選擇檔案
                </Button>
              </Flex>
            </>
          )}

        {/* 驗證碼輸入頁：正常流程 */}
        {status === Estatus.status && uploadStatus === EuploadStatus.normal && (
          <>
            <MainPanel>
              <Flex
                direction="column"
                align="center"
                justify="center"
                gap="30px"
              >
                <Flex direction="column" align="center" gap="10px">
                  <Image
                    src={uploadOkIcon}
                    alt="upload success"
                    maxW="200px"
                    w="100%"
                  />
                  <Text
                    fontFamily="Open Sans"
                    fontWeight={600}
                    fontSize="20px"
                    lineHeight="120%"
                    letterSpacing="0.01em"
                    color="#28A138"
                    textAlign="center"
                  >
                    上傳成功
                  </Text>
                </Flex>
                {FileNameBox}
              </Flex>
            </MainPanel>

            <Flex align="center" justify="center" gap="16px" wrap="nowrap">
              <Input
                value={captchaChallenge}
                onChange={(e) => setCaptchaChallenge(e.target.value)}
                placeholder="請輸入驗證碼"
                maxLength={5}
                w="100%"
                maxW="220px"
                minH="37px"
                px="12px"
                py="6px"
                bg="#FFFFFF"
                border="1px solid #A8B8C2"
                borderRadius="3px"
                fontFamily="Open Sans"
                fontWeight={400}
                fontSize="14px"
                lineHeight="150%"
                letterSpacing="0.01em"
                color="#4F6470"
                _placeholder={{ color: "#4F6470" }}
                _hover={{ borderColor: "#A8B8C2" }}
                _focus={{ borderColor: "#A8B8C2", boxShadow: "none" }}
              />

              <Flex
                w="93.63px"
                minH="35px"
                align="center"
                justify="center"
                overflow="hidden"
              >
                {captchaSrc && (
                  <img
                    src={captchaSrc}
                    alt="captcha"
                    style={{
                      display: "block",
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                    }}
                  />
                )}
              </Flex>

              <Button
                variant="unstyled"
                minW="24px"
                minH="24px"
                display="inline-flex"
                alignItems="center"
                justifyContent="center"
                aria-label="refresh captcha"
                onClick={handleRefreshCaptcha}
              >
                <Image
                  src={refreshIcon}
                  alt="refresh captcha"
                  w="24px"
                  h="24px"
                />
              </Button>

              <Button
                px="12px"
                py="8px"
                minH="37px"
                bg={canSubmit ? "#00AAFF" : "#E6F7FF"}
                color="#FFFFFF"
                borderRadius="4px"
                fontFamily="Open Sans"
                fontWeight={400}
                fontSize="14px"
                lineHeight="150%"
                letterSpacing="0.01em"
                isDisabled={!canSubmit}
                _hover={{ bg: canSubmit ? "#00AAFF" : "#E6F7FF" }}
                _active={{ bg: canSubmit ? "#00AAFF" : "#E6F7FF" }}
                _disabled={{
                  opacity: 1,
                  color: "#FFFFFF",
                  bg: "#E6F7FF",
                  cursor: "not-allowed",
                }}
                onClick={handleStartVerify}
              >
                開始驗證
              </Button>
            </Flex>
          </>
        )}

        {/* 驗證結果頁 */}
        {status === Estatus.result && (
          <>
            <MainPanel>
              <Flex
                direction="column"
                align="center"
                justify="center"
                gap="30px"
              >
                <Flex direction="column" align="center" gap="10px">
                  <Image
                    src={resultVisual?.src ?? verifyFailIcon}
                    alt="verify result"
                    maxW="200px"
                    w="100%"
                  />
                  <Text
                    fontFamily="Open Sans"
                    fontWeight={600}
                    fontSize="20px"
                    lineHeight="120%"
                    letterSpacing="0.01em"
                    color={resultVisual?.color ?? "#E02020"}
                    textAlign="center"
                  >
                    {resultVisual?.title ?? t("upload.verifyFailed")}
                  </Text>
                </Flex>
                {FileNameBox}
              </Flex>
            </MainPanel>

            <Flex justify="center">
              <Button
                px="12px"
                py="8px"
                minH="37px"
                bg="#00AAFF"
                color="#FFFFFF"
                borderRadius="4px"
                fontFamily="Open Sans"
                fontWeight={400}
                fontSize="14px"
                lineHeight="150%"
                letterSpacing="0.01em"
                _hover={{ bg: "#00AAFF" }}
                _active={{ bg: "#00AAFF" }}
                onClick={resetToFirstStep}
              >
                再次驗證
              </Button>
            </Flex>
          </>
        )}
      </Flex>
    </>
  );
};

export default UploadSection;
